import {
  useProduct,
  useCreateProduct,
  useUpdateProduct,
  useProductCategories,
  useProductSubcategories,
  uploadFile,
} from '@/api';
import {
  Button,
  Input,
  Textarea,
  Avatar,
  AvatarImage,
  AvatarFallback,
  Label,
  FormSelect,
} from '@/components/atoms';
import { FormField } from '@/components/molecules';
import { MediaLibraryMultiSelect } from './media-library-multi-select';
import {
  productCreationSchema,
  productUpdateSchema,
  type ProductCreationFormData,
  type ProductUpdateFormData,
} from '@/validation-schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import toast from 'react-hot-toast';
import { ImagePlus } from 'lucide-react';
import { CategoryModal, CREATE_CATEGORY_VALUE } from './category-modal';
import { usePlanGate } from '@/components/organisms';

interface ProductCreationFormProps {
  id?: number;
  mode: 'create' | 'update' | 'view';
  toggleOpen: () => void;
}

export const ProductCreationForm = ({
  id,
  mode,
  toggleOpen,
}: ProductCreationFormProps) => {
  const isReadOnly = mode === 'view';
  const { allowsCount, entitlements } = usePlanGate();
  const [isLoading, setIsLoading] = useState(false);
  const [images, setImages] = useState<(File | null)[]>([
    null,
    null,
    null,
    null,
  ]);
  const [libraryImageIds, setLibraryImageIds] = useState<number[]>([]);
  const [removedImageIds, setRemovedImageIds] = useState<number[]>([]);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  const multiFileInputRef = useRef<HTMLInputElement>(null);
  const [pendingSlot, setPendingSlot] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    reset,
    trigger,
    watch,
  } = useForm<ProductCreationFormData | ProductUpdateFormData>({
    resolver: yupResolver(
      mode === 'update' ? productUpdateSchema : productCreationSchema
    ) as Resolver<ProductCreationFormData | ProductUpdateFormData>,
    mode: 'onChange',
    defaultValues: {
      categoryId: undefined,
      subcategoryId: undefined,
      variations: '',
      colour: '',
      size: '',
      sku: '',
      weight: '',
    },
  });

  const selectedCategoryId = watch('categoryId');
  const selectedSubcategoryId = watch('subcategoryId');
  const numericCategoryId = selectedCategoryId
    ? Number(selectedCategoryId)
    : undefined;
  const { data: categories = [] } = useProductCategories();
  const { data: subcategories = [] } = useProductSubcategories(
    numericCategoryId,
    !!numericCategoryId
  );

  const { data: product } = useProduct(mode !== 'create' ? id : undefined);

  const selectedCategory = useMemo(() => {
    if (!numericCategoryId) return undefined;

    const fromList = categories.find(
      (category) => Number(category.id) === numericCategoryId
    );
    if (fromList) return fromList;

    if (
      product?.category &&
      Number(product.category.id) === numericCategoryId
    ) {
      return product.category;
    }

    return undefined;
  }, [categories, numericCategoryId, product?.category]);

  const subcategoryOptions = useMemo(() => {
    const options = subcategories.map((subcategory) => ({
      value: String(subcategory.id),
      label: subcategory.name,
    }));

    const productSubcategory = product?.subcategory;
    if (
      productSubcategory &&
      !options.some((option) => option.value === String(productSubcategory.id))
    ) {
      options.push({
        value: String(productSubcategory.id),
        label: productSubcategory.name,
      });
    }

    return options;
  }, [subcategories, product?.subcategory]);

  const categoryDisplayName =
    product?.category?.name ?? selectedCategory?.name ?? '—';

  const subcategoryDisplayName = useMemo(() => {
    const productSubcategory = product?.subcategory;
    if (productSubcategory && productSubcategory.id === selectedSubcategoryId) {
      return productSubcategory.name;
    }
    return (
      subcategoryOptions.find(
        (option) => option.value === String(selectedSubcategoryId)
      )?.label ?? '—'
    );
  }, [product?.subcategory, selectedSubcategoryId, subcategoryOptions]);

  const categoryOptions = useMemo(() => {
    const options = categories.map((category) => ({
      value: String(category.id),
      label: category.name,
    }));

    if (
      product?.category &&
      !options.some((option) => option.value === String(product.category!.id))
    ) {
      options.unshift({
        value: String(product.category.id),
        label: product.category.name,
      });
    }

    return [
      ...options,
      { value: CREATE_CATEGORY_VALUE, label: 'Create category' },
    ];
  }, [categories, product?.category]);

  useEffect(() => {
    if (product && (mode === 'update' || mode === 'view')) {
      setValue('name', product.name);
      setValue('shortDescription', product.shortDescription);
      setValue('description', product.description);
      setValue('price', product.price);
      setValue('costPrice', product.costPrice);
      setValue('discountedPrice', product.discountedPrice);
      setValue('quantity', product.quantity);
      const categoryId = product.categoryId ?? product.category?.id ?? null;
      const subcategoryId =
        product.subcategoryId ?? product.subcategory?.id ?? null;
      setValue('categoryId', categoryId);
      setValue('subcategoryId', subcategoryId);
      setValue('variations', product.variations ?? '');
      setValue('colour', product.colors?.[0]?.name ?? '');
      setValue('size', product.sizes?.[0] ?? '');
      setValue('sku', product.sku ?? '');
      setValue(
        'weight',
        product.weight != null && product.weight !== ''
          ? String(product.weight)
          : ''
      );
      // Trigger validation after setting values
      trigger();
      setImages([null, null, null, null]);
      setRemovedImageIds([]);
    } else {
      reset({
        categoryId: undefined,
        subcategoryId: undefined,
        variations: '',
        colour: '',
        size: '',
        sku: '',
        weight: '',
      });
      setImages([null, null, null, null]);
      setRemovedImageIds([]);
    }
  }, [product, mode, setValue, reset, trigger]);

  const { mutate: create, isPending } = useCreateProduct(toggleOpen);

  const { mutate: update, isPending: isUpdatePending } =
    useUpdateProduct(toggleOpen);

  const isSlotOccupied = (idx: number) => {
    if (images[idx]) return true;
    if (
      product?.productImages[idx] &&
      !removedImageIds.includes(product.productImages[idx].image.id)
    )
      return true;
    return false;
  };

  const currentImageCount = () =>
    images.filter((_, idx) => isSlotOccupied(idx)).length +
    libraryImageIds.length;

  const handleImageClick = (idx: number) => {
    if (isReadOnly) return;
    if (isSlotOccupied(idx)) return;
    if (!allowsCount('imagesPerProduct', idx + 1)) return;
    setPendingSlot(idx);
    if (multiFileInputRef.current) {
      multiFileInputRef.current.value = '';
      multiFileInputRef.current.click();
    }
  };

  const handleBulkAdd = () => {
    if (isReadOnly) return;
    if (!allowsCount('imagesPerProduct', currentImageCount() + 1)) return;
    setPendingSlot(null);
    if (multiFileInputRef.current) {
      multiFileInputRef.current.value = '';
      multiFileInputRef.current.click();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) return;
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remaining = Math.max(
      0,
      entitlements.maxImagesPerProduct - currentImageCount()
    );
    const acceptedFiles = files.slice(0, remaining);
    if (files.length > remaining) {
      allowsCount('imagesPerProduct', currentImageCount() + files.length);
    }
    if (acceptedFiles.length === 0) return;

    setImages((prev) => {
      const updated = [...prev];

      if (pendingSlot !== null) {
        // Single-slot mode: assign first file to the clicked slot
        const existingId = product?.productImages[pendingSlot]?.image.id;
        if (existingId) {
          setRemovedImageIds((prev) =>
            prev.includes(existingId) ? prev : [...prev, existingId]
          );
        }
        updated[pendingSlot] = acceptedFiles[0];
      } else {
        // Bulk mode: distribute files across empty slots
        let fileIdx = 0;
        for (
          let i = 0;
          i < updated.length && fileIdx < acceptedFiles.length;
          i++
        ) {
          const slotHasExisting =
            product?.productImages[i] &&
            !removedImageIds.includes(product.productImages[i].image.id);
          if (!updated[i] && !slotHasExisting) {
            updated[i] = acceptedFiles[fileIdx];
            fileIdx++;
          }
        }
      }

      return updated;
    });

    setPendingSlot(null);
  };

  const handleImageRemove = (idx: number) => {
    if (isReadOnly) return;
    const existingId = product?.productImages[idx]?.image.id;
    if (existingId) {
      setRemovedImageIds((prev) =>
        prev.includes(existingId) ? prev : [...prev, existingId]
      );
    }

    setImages((prev) => {
      const updated = [...prev];
      updated[idx] = null;
      return updated;
    });
  };

  const handleCategoryCreated = ({
    categoryId,
    subcategoryId,
  }: {
    categoryId: number;
    subcategoryId: number;
  }) => {
    setValue('categoryId', categoryId, { shouldValidate: true });
    if (subcategoryId) {
      setValue('subcategoryId', subcategoryId, { shouldValidate: true });
    }
    trigger(['categoryId', 'subcategoryId']);
  };

  const submit = async (
    productData: ProductCreationFormData | ProductUpdateFormData
  ) => {
    if (isReadOnly) return;
    let newImageIds: number[] = [];
    let existingImageIds: number[] = [];

    const validImages = images.filter((image): image is File => image !== null);

    if (validImages.length > 0) {
      setIsLoading(true);

      try {
        const formData = new FormData();

        validImages.forEach((file) => {
          formData.append('file', file);
        });

        const res = await uploadFile(formData);

        newImageIds = res.data.map((uploadedFile) => uploadedFile.id);
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : 'Failed to upload images'
        );
      } finally {
        setIsLoading(false);
      }
    }

    // Start from existing product images (update mode) and drop any the user removed
    if (mode === 'update' && product?.productImages) {
      existingImageIds = product.productImages
        .map((productImage) => productImage.image.id)
        .filter((id) => !removedImageIds.includes(id));
    }

    const allImageIds = [
      ...new Set([...existingImageIds, ...newImageIds, ...libraryImageIds]),
    ];

    const {
      subcategoryId,
      categoryId,
      colour,
      size,
      variations,
      sku,
      weight,
      ...rest
    } = productData;

    const trimmedColour = colour?.trim();
    const trimmedSize = size?.trim();
    const trimmedVariations = variations?.trim();
    const trimmedSku = sku?.trim();
    const trimmedWeight = weight?.trim();

    const payload = {
      ...rest,
      images: allImageIds,
      ...(trimmedVariations ? { variations: trimmedVariations } : {}),
      ...(trimmedColour
        ? { colors: [{ code: '#000000', name: trimmedColour }] }
        : {}),
      ...(trimmedSize ? { sizes: [trimmedSize] } : {}),
      ...(trimmedSku ? { sku: trimmedSku } : {}),
      ...(trimmedWeight ? { weight: trimmedWeight } : {}),
    };

    if (mode === 'update' && id) {
      update({
        id,
        ...payload,
        categoryId: categoryId ?? null,
        subcategoryId: subcategoryId ?? null,
      });
    } else {
      create({
        ...payload,
        categoryId: categoryId!,
        ...(subcategoryId ? { subcategoryId } : {}),
      });
    }

    setImages([null, null, null, null]);
    setLibraryImageIds([]);
    setRemovedImageIds([]);
  };

  return (
    <>
      <form onSubmit={handleSubmit(submit)} className="grid grid-cols-2 gap-3">
        <FormField label="Product Images" className="col-span-2">
          <div className="flex gap-2.5">
            {images.map((img, idx) => {
              const occupied = isSlotOccupied(idx);
              return (
                <div key={idx} className="flex flex-col items-center gap-1">
                  <Avatar
                    className={`size-16 sm:size-20 md:size-24 aspect-square rounded-md border ${
                      occupied ? 'cursor-default' : 'cursor-pointer'
                    }`}
                    onClick={() => handleImageClick(idx)}
                  >
                    <AvatarImage
                      alt={`product image ${idx + 1}`}
                      className="object-cover rounded-md"
                      src={
                        img
                          ? URL.createObjectURL(img)
                          : product?.productImages[idx] &&
                              !removedImageIds.includes(
                                product.productImages[idx].image.id
                              )
                            ? product.productImages[idx].image.url
                            : undefined
                      }
                    />
                    <AvatarFallback className="text-xs aspect-square rounded-md">
                      Add
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs">Image {idx + 1}</span>
                  {occupied && (
                    <Button
                      variant="link"
                      type="button"
                      onClick={() => handleImageRemove(idx)}
                      className="text-[10px] text-destructive h-fit!"
                      disabled={isReadOnly}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          <input
            ref={multiFileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileSelect}
            disabled={isReadOnly}
          />
          {images.some((_, idx) => !isSlotOccupied(idx)) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2 gap-1.5"
              onClick={handleBulkAdd}
              disabled={isReadOnly}
            >
              <ImagePlus className="size-4" />
              Add Images
            </Button>
          )}
          <div className="mt-4 space-y-2">
            <Label>Attach from media library</Label>
            <MediaLibraryMultiSelect
              value={libraryImageIds}
              onChange={(ids) => {
                const slotCount = images.filter((_, idx) =>
                  isSlotOccupied(idx)
                ).length;
                if (!allowsCount('imagesPerProduct', slotCount + ids.length)) {
                  const room = Math.max(
                    0,
                    entitlements.maxImagesPerProduct - slotCount
                  );
                  setLibraryImageIds(ids.slice(0, room));
                  return;
                }
                setLibraryImageIds(ids);
              }}
              disabled={isReadOnly}
              placeholder="Select images from gallery"
            />
          </div>
        </FormField>
        <FormField
          label="Product name"
          className="col-span-2"
          error={errors.name?.message}
        >
          <Input
            placeholder="eg. Apple MacBook Pro"
            {...register('name')}
            disabled={isReadOnly}
          />
        </FormField>
        <FormField
          label="Product description"
          className="col-span-2"
          error={errors.shortDescription?.message}
        >
          <Input
            placeholder="eg. 2025 M4 chip"
            {...register('shortDescription')}
            disabled={isReadOnly}
          />
        </FormField>
        <FormField
          label="More details (optional)"
          className="col-span-2"
          error={errors.description?.message}
        >
          <Textarea
            placeholder="Add a longer description"
            {...register('description')}
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Category"
          className="col-span-2"
          error={errors.categoryId?.message}
        >
          {isReadOnly ? (
            <Input value={categoryDisplayName} disabled />
          ) : (
            <div className="flex gap-2">
              <div className="flex-1">
                <Controller
                  name="categoryId"
                  control={control}
                  render={({ field }) => (
                    <FormSelect
                      placeholder="Select category"
                      value={field.value ? String(field.value) : ''}
                      onChange={(value) => {
                        if (value === CREATE_CATEGORY_VALUE) {
                          setCategoryModalOpen(true);
                          return;
                        }
                        field.onChange(value ? Number(value) : null);
                        setValue('subcategoryId', null, {
                          shouldValidate: true,
                        });
                      }}
                      options={categoryOptions}
                    />
                  )}
                />
              </div>
              {selectedCategoryId && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setValue('categoryId', null, {
                      shouldValidate: true,
                    });
                    setValue('subcategoryId', null, {
                      shouldValidate: true,
                    });
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          )}
        </FormField>

        <FormField
          label="Subcategory (optional)"
          className="col-span-2"
          error={errors.subcategoryId?.message}
        >
          {isReadOnly ? (
            <Input
              value={selectedSubcategoryId ? subcategoryDisplayName : '—'}
              disabled
            />
          ) : (
            <div className="flex gap-2">
              <div className="flex-1">
                <Controller
                  name="subcategoryId"
                  control={control}
                  render={({ field }) => (
                    <FormSelect
                      placeholder={
                        selectedCategoryId
                          ? 'Select subcategory (optional)'
                          : 'Select a category first'
                      }
                      value={field.value ? String(field.value) : ''}
                      onChange={(value) =>
                        field.onChange(value ? Number(value) : null)
                      }
                      options={subcategoryOptions}
                      disabled={!selectedCategoryId}
                    />
                  )}
                />
              </div>
              {selectedSubcategoryId && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setValue('subcategoryId', null, {
                      shouldValidate: true,
                    });
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          )}
        </FormField>

        <FormField
          label="Product variations (optional)"
          className="col-span-2"
          error={errors.variations?.message}
          infoTip='List variation types separated by "|", e.g. Colour|Size'
        >
          <Input
            placeholder="eg. Colour|Size"
            {...register('variations')}
            disabled={isReadOnly}
          />
        </FormField>
        <FormField
          label="Colour (optional)"
          className="col-span-1"
          error={errors.colour?.message}
        >
          <Input
            placeholder="eg. Green"
            {...register('colour')}
            disabled={isReadOnly}
          />
        </FormField>
        <FormField
          label="Size (optional)"
          className="col-span-1"
          error={errors.size?.message}
        >
          <Input
            placeholder="eg. M"
            {...register('size')}
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Selling price"
          className="col-span-2"
          error={errors.price?.message}
          infoTip="The price customers will pay for this product. This is what will be displayed to buyers on your store website."
        >
          <Input
            placeholder="eg. ₦1,200"
            type="number"
            {...register('price')}
            disabled={isReadOnly}
          />
        </FormField>
        <FormField
          label="Cost price (optional)"
          className="col-span-1"
          error={errors.costPrice?.message}
          infoTip="The amount you bought the product for. This is only for your inventory tracking and profit calculation. Customers don't see this."
        >
          <Input
            placeholder="eg. 800"
            type="number"
            {...register('costPrice')}
            disabled={isReadOnly}
          />
        </FormField>
        <FormField
          label="Discounted price (optional)"
          className="col-span-1"
          error={errors.discountedPrice?.message}
          infoTip="The percentage you want to take off the selling price as a discount. Enter the percentage and the system will automatically calculate and show the final discounted price to customers."
        >
          <Input
            placeholder="eg. ₦1,000"
            type="number"
            {...register('discountedPrice')}
            disabled={isReadOnly}
          />
        </FormField>
        <FormField
          label="Stock quantity"
          className="col-span-2"
          error={errors.quantity?.message}
        >
          <Input
            placeholder="eg. 12"
            type="number"
            {...register('quantity')}
            disabled={isReadOnly}
          />
        </FormField>
        <FormField
          label="SKU (optional)"
          className="col-span-1"
          error={errors.sku?.message}
        >
          <Input
            placeholder="eg. SLT-TOTE-001"
            {...register('sku')}
            disabled={isReadOnly}
          />
        </FormField>
        <FormField
          label="Weight (optional)"
          className="col-span-1"
          error={errors.weight?.message}
          infoTip="Product weight, e.g. in kg"
        >
          <Input
            placeholder="eg. 0.6"
            {...register('weight')}
            disabled={isReadOnly}
          />
        </FormField>

        <Button
          className="col-span-2"
          label={
            mode === 'create'
              ? 'Create product'
              : mode === 'update'
                ? 'Update product'
                : 'View only'
          }
          size="lg"
          isLoading={isPending || isLoading || isUpdatePending}
          disabled={isReadOnly || !isValid}
        />
      </form>

      <CategoryModal
        open={categoryModalOpen}
        onOpenChange={setCategoryModalOpen}
        onSuccess={handleCategoryCreated}
      />
    </>
  );
};
